import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, money, errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const fields = [
  { k: "name", label: "Full name", span: "sm:col-span-2" },
  { k: "address", label: "Street address", span: "sm:col-span-2" },
  { k: "city", label: "City" },
  { k: "postal_code", label: "Postal code" },
  { k: "phone", label: "Phone (for delivery)", type: "tel" },
  { k: "country", label: "Country", span: "sm:col-span-2" },
];

function Field({ label, value, onChange, testid, type = "text", className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="eyebrow !text-[10px] text-[var(--ink-soft)]">{label}</span>
      <input required type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 h-12 w-full border-b border-[var(--ink)]/30 bg-transparent text-base outline-none transition-colors focus:border-[var(--terra)]" data-testid={testid} />
    </label>
  );
}

export default function Checkout() {
  const { items, subtotal, shipping, total } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [ship, setShip] = useState({ name: "", address: "", city: "", postal_code: "", country: "" });
  const [busy, setBusy] = useState(false);
  const [paypal, setPaypal] = useState(null);

  useEffect(() => {
    api.get("/payments/methods").then((r) => setPaypal(r.data)).catch(() => setPaypal({ paypal: false }));
  }, []);

  useEffect(() => {
    if (user?.email) { setEmail(user.email); setShip((s) => ({ ...s, name: s.name || user.name || "" })); }
  }, [user]);

  const paypalReady = !!(email && ship.name && ship.address && ship.city && ship.postal_code && ship.country);

  const paypalCreate = async () => {
    try {
      const { data } = await api.post("/checkout/paypal", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        email, shipping: ship, origin_url: window.location.origin,
      });
      return data.paypal_order_id;
    } catch (err) {
      toast.error(errMsg(err));
      throw err;
    }
  };

  const paypalApprove = async (data) => {
    try {
      const res = await api.post("/checkout/paypal/capture", { paypal_order_id: data.orderID });
      nav(`/payment/success?session_id=${data.orderID}&order=${res.data.order_number || ""}`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        email, shipping: ship, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  if (!items.length) return (
    <div className="py-40 text-center" data-testid="checkout-empty"><p className="font-display text-4xl">Your bag is empty.</p><Link to="/shop" className="link-u mt-4 inline-block text-sm font-semibold">Go shopping</Link></div>
  );

  return (
    <form onSubmit={submit} className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-32 pt-12 md:px-10 lg:grid-cols-12 lg:gap-20 lg:pt-20" data-testid="checkout-form">
      <div className="lg:col-span-7">
        <h1 className="font-display text-5xl tracking-tight sm:text-6xl">Checkout</h1>
        {!user && <p className="mt-4 text-sm text-[var(--ink-soft)]">Checking out as a guest. <Link to="/account" className="link-u font-semibold text-[var(--ink)]" data-testid="checkout-signin-link">Sign in</Link> to track orders in your account.</p>}
        <p className="eyebrow mt-12 text-[var(--terra-deep)]">01 — Contact</p>
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="checkout-email-input" className="mt-6" />
        <p className="eyebrow mt-14 text-[var(--terra-deep)]">02 — Shipping</p>
        <div className="mt-6 grid gap-8 sm:grid-cols-2">
          {fields.map((f) => (
            <Field key={f.k} label={f.label} value={ship[f.k]} onChange={(v) => setShip((s) => ({ ...s, [f.k]: v }))} testid={`checkout-${f.k.replace("_", "-")}-input`} className={f.span || ""} />
          ))}
        </div>
        <p className="eyebrow mt-14 text-[var(--terra-deep)]">03 — Payment</p>
        {paypal?.paypal ? (
          <div className="mt-6">
            <button disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--ink)] py-4 text-sm font-semibold text-[var(--bone)] transition-colors hover:bg-[var(--terra-deep)] disabled:opacity-60" data-testid="checkout-pay-card-button">
              <Lock size={16} /> {busy ? "Redirecting to secure payment…" : `Pay ${money(total)} by card`}
            </button>
            <div className="my-6 flex items-center gap-4 text-xs text-[var(--ink-soft)]">
              <span className="h-px flex-1 bg-[var(--line)]" /> or pay with <span className="h-px flex-1 bg-[var(--line)]" />
            </div>
            {paypalReady ? (
              <div className="rounded-2xl border border-[var(--line)] bg-white/50 p-3" data-testid="paypal-buttons-box">
                <PayPalScriptProvider options={{ clientId: paypal.client_id, currency: "USD", intent: "capture" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "pill", color: "gold", height: 50 }}
                    forceReRender={[total, email, ship]}
                    createOrder={paypalCreate}
                    onApprove={paypalApprove}
                    onError={(e) => { console.error("PayPal error:", e); toast.error("PayPal hit a snag. Try again or pay by card."); }}
                    onCancel={() => toast("PayPal window closed — your bag is saved.")}
                    data-testid="paypal-buttons"
                  />
                </PayPalScriptProvider>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  const el = document.querySelector("[data-testid=checkout-email-input]");
                  el?.scrollIntoView({ behavior: "smooth", block: "center" });
                  setTimeout(() => el?.focus({ preventScroll: true }), 600);
                }}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-dashed border-[var(--ink)]/40 py-3.5 text-xs font-semibold text-[var(--ink-soft)] transition-colors hover:border-[var(--ink)] hover:text-[var(--ink)]"
                data-testid="paypal-gate-hint"
              >
                <Lock size={14} /> Fill in your details above to unlock PayPal
              </button>
            )}
            <p className="mt-4 text-center text-xs text-[var(--ink-soft)]">Cards run through Stripe. PayPal runs through PayPal. Both are encrypted.</p>
          </div>
        ) : (
          <>
            <p className="mt-4 text-sm leading-relaxed text-[var(--ink-soft)]">You'll enter your debit or credit card on Stripe's secure payment page.</p>
          </>
        )}
      </div>
      <aside className="lg:col-span-5">
        <div className="rounded-3xl bg-[var(--ink)] p-6 text-[var(--bone)] md:p-8 lg:sticky lg:top-28" data-testid="checkout-summary">
          <p className="eyebrow text-stone-400">Order summary</p>
          <div className="mt-6 space-y-4">
            {items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-4">
                <img src={i.image} alt={i.name} className="h-16 w-14 rounded-lg object-cover" />
                <div className="flex-1 text-sm"><p className="font-semibold">{i.name}</p><p className="text-stone-400">Qty {i.quantity}</p></div>
                <span className="text-sm">{money(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 space-y-2 border-t border-stone-700 pt-6 text-sm">
            <div className="flex justify-between"><span className="text-stone-300">Subtotal</span><span>{money(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-stone-300">Shipping</span><span>{shipping ? money(shipping) : "Free"}</span></div>
            <div className="flex justify-between pt-3 text-lg font-semibold"><span>Total</span><span data-testid="checkout-total">{money(total)}</span></div>
          </div>
          <button disabled={busy} className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--terra)] py-4 text-sm font-semibold text-[#241a06] transition-colors hover:bg-[var(--terra-deep)] disabled:opacity-60" data-testid="checkout-pay-button">
            <Lock size={16} /> {busy ? "Redirecting to secure payment…" : `Pay ${money(total)} by card`}
          </button>
          <p className="mt-4 text-center text-xs text-stone-400">Taxes, if any, are calculated on the payment page.</p>
        </div>
      </aside>
    </form>
  );
}
